/* istanbul ignore file */
/* tslint:disable */
/* eslint-disable */
import type { avy_api_pkg_segment_PublishResourceRequest } from "../models/avy_api_pkg_segment_PublishResourceRequest"
import type { config_GetFeatureConfigListResponse } from "../models/config_GetFeatureConfigListResponse"
import type { knowledgebase_ConnectAgentsRequest } from "../models/knowledgebase_ConnectAgentsRequest"
import type { knowledgebase_CreateKnowledgeItemRequest } from "../models/knowledgebase_CreateKnowledgeItemRequest"
import type { knowledgebase_KnowledgeItem } from "../models/knowledgebase_KnowledgeItem"
import type { knowledgebase_KnowledgeItemCardResponse } from "../models/knowledgebase_KnowledgeItemCardResponse"
import type { knowledgebase_KnowledgeItemListRequest } from "../models/knowledgebase_KnowledgeItemListRequest"
import type { knowledgebase_KnowledgeItemListResponse } from "../models/knowledgebase_KnowledgeItemListResponse"
import type { knowledgebase_KnowledgeItemWithRelations } from "../models/knowledgebase_KnowledgeItemWithRelations"
import type { knowledgebase_UpdateKnowledgeItemRequest } from "../models/knowledgebase_UpdateKnowledgeItemRequest"
import type { tags_ApplyTagsRequest } from "../models/tags_ApplyTagsRequest"

import type { CancelablePromise } from "../core/CancelablePromise"
import { OpenAPI } from "../core/OpenAPI"
import { request as __request } from "../core/request"

export class KnowledgeHubAdminService {
    /**
     * Create a new knowledge item
     * Create a new knowledge item with the provided data
     * @returns knowledgebase_KnowledgeItem OK
     * @throws ApiError
     */
    public static postV1AdminKnowledgebaseKnowledgeItems({
        request,
    }: {
        /** Knowledge item creation request **/
        request: knowledgebase_CreateKnowledgeItemRequest
    }): CancelablePromise<knowledgebase_KnowledgeItem> {
        return __request(OpenAPI, {
            method: "POST",
            url: "/v1/admin/knowledgebase/knowledge-items",
            body: request,
        })
    }

    /**
     * Get knowledge item cards
     * Get cards for knowledge items
     * @returns knowledgebase_KnowledgeItemCardResponse OK
     * @throws ApiError
     */
    public static postV1AdminKnowledgebaseKnowledgeItemsCards(): CancelablePromise<knowledgebase_KnowledgeItemCardResponse> {
        return __request(OpenAPI, {
            method: "POST",
            url: "/v1/admin/knowledgebase/knowledge-items/cards",
        })
    }

    /**
     * Get connectable agents for knowledge items
     * Get a list of agents that can be connected to knowledge items
     * @returns config_GetFeatureConfigListResponse OK
     * @throws ApiError
     */
    public static getV1AdminKnowledgebaseKnowledgeItemsConnectableAgents({
        agentType,
    }: {
        /** Type of agent instances to get connectable agents for **/
        agentType: string
    }): CancelablePromise<config_GetFeatureConfigListResponse> {
        return __request(OpenAPI, {
            method: "GET",
            url: "/v1/admin/knowledgebase/knowledge-items/connectable-agents",
            query: {
                agent_type: agentType,
            },
        })
    }

    /**
     * List knowledge items
     * List knowledge items with optional filtering
     * @returns knowledgebase_KnowledgeItemListResponse OK
     * @throws ApiError
     */
    public static postV1AdminKnowledgebaseKnowledgeItemsList({
        request,
    }: {
        /** Filter configuration **/
        request: knowledgebase_KnowledgeItemListRequest
    }): CancelablePromise<knowledgebase_KnowledgeItemListResponse> {
        return __request(OpenAPI, {
            method: "POST",
            url: "/v1/admin/knowledgebase/knowledge-items/list",
            body: request,
        })
    }

    /**
     * Get a knowledge item by ID
     * Retrieve a knowledge item by its ID
     * @returns knowledgebase_KnowledgeItemWithRelations OK
     * @throws ApiError
     */
    public static getV1AdminKnowledgebaseKnowledgeItems({
        knowledgeItemId,
    }: {
        /** Knowledge item ID **/
        knowledgeItemId: number
    }): CancelablePromise<knowledgebase_KnowledgeItemWithRelations> {
        return __request(OpenAPI, {
            method: "GET",
            url: "/v1/admin/knowledgebase/knowledge-items/{knowledge_item_id}",
            path: {
                knowledge_item_id: knowledgeItemId,
            },
        })
    }

    /**
     * Update a knowledge item
     * Update an existing knowledge item by ID
     * @returns knowledgebase_KnowledgeItem OK
     * @throws ApiError
     */
    public static putV1AdminKnowledgebaseKnowledgeItems({
        knowledgeItemId,
        request,
    }: {
        /** Knowledge item ID **/
        knowledgeItemId: number
        /** Knowledge item update request **/
        request: knowledgebase_UpdateKnowledgeItemRequest
    }): CancelablePromise<knowledgebase_KnowledgeItem> {
        return __request(OpenAPI, {
            method: "PUT",
            url: "/v1/admin/knowledgebase/knowledge-items/{knowledge_item_id}",
            path: {
                knowledge_item_id: knowledgeItemId,
            },
            body: request,
        })
    }

    /**
     * Delete a knowledge item
     * Delete a knowledge item by ID
     * @returns any OK
     * @throws ApiError
     */
    public static deleteV1AdminKnowledgebaseKnowledgeItems({
        knowledgeItemId,
    }: {
        /** Knowledge item ID **/
        knowledgeItemId: number
    }): CancelablePromise<any> {
        return __request(OpenAPI, {
            method: "DELETE",
            url: "/v1/admin/knowledgebase/knowledge-items/{knowledge_item_id}",
            path: {
                knowledge_item_id: knowledgeItemId,
            },
        })
    }

    /**
     * Set agent types for a knowledge item
     * Set agent types for a knowledge item by ID
     * @returns any OK
     * @throws ApiError
     */
    public static putV1AdminKnowledgebaseKnowledgeItemsConnectAgents({
        knowledgeItemId,
        request,
    }: {
        /** Knowledge item ID **/
        knowledgeItemId: number
        /** Request body to set agent types **/
        request: knowledgebase_ConnectAgentsRequest
    }): CancelablePromise<any> {
        return __request(OpenAPI, {
            method: "PUT",
            url: "/v1/admin/knowledgebase/knowledge-items/{knowledge_item_id}/connect-agents",
            path: {
                knowledge_item_id: knowledgeItemId,
            },
            body: request,
        })
    }

    /**
     * Publish a knowledge item
     * Publish a knowledge item to specified segments
     * @returns any OK
     * @throws ApiError
     */
    public static putV1AdminKnowledgebaseKnowledgeItemsPublish({
        knowledgeItemId,
        request,
    }: {
        /** Knowledge item ID **/
        knowledgeItemId: number
        /** Publish request containing segment IDs **/
        request: avy_api_pkg_segment_PublishResourceRequest
    }): CancelablePromise<any> {
        return __request(OpenAPI, {
            method: "PUT",
            url: "/v1/admin/knowledgebase/knowledge-items/{knowledge_item_id}/publish",
            path: {
                knowledge_item_id: knowledgeItemId,
            },
            body: request,
        })
    }

    /**
     * Apply tags to a knowledge item
     * Apply tags to a knowledge item by ID
     * @returns any OK
     * @throws ApiError
     */
    public static putV1AdminKnowledgebaseKnowledgeItemsTag({
        knowledgeItemId,
        request,
    }: {
        /** Knowledge item ID **/
        knowledgeItemId: number
        /** Request body containing tag IDs **/
        request: tags_ApplyTagsRequest
    }): CancelablePromise<any> {
        return __request(OpenAPI, {
            method: "PUT",
            url: "/v1/admin/knowledgebase/knowledge-items/{knowledge_item_id}/tag",
            path: {
                knowledge_item_id: knowledgeItemId,
            },
            body: request,
        })
    }
}
